import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal } from "reactstrap";
import {
  formattedDate,
  formattedTime,
} from "../../components/formatted/FormattedDateAndTime";
import { validateMidCycleFluxChange } from "../../services/container";

/**
 * Mid-cycle flux-change alert.
 * - default: full badge; click opens modal
 * - variant="table": compact chip; click opens modal with details + Metrics relaunch
 * Supports changeType: assignment | conditions
 */
const MidCycleFluxChangeBadge = ({
  container,
  onValidated,
  showValidateButton = true,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);
  const [clearedLocally, setClearedLocally] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const alert = container?.midCycleFluxChange;

  if (!alert?.active || clearedLocally) return null;

  const isConditions = alert.changeType === "conditions";
  const previousLabel =
    alert.previousLabel || t("container.midCycleFluxUnknown");
  const newLabel = alert.newLabel || t("container.midCycleFluxUnknown");
  const modifiedAtLabel = alert.modifiedAt
    ? `${formattedDate(alert.modifiedAt, t, true, false, true)} ${formattedTime(alert.modifiedAt)}`
    : null;

  const chipLabel = isConditions
    ? t("container.midCycleFluxConditionsChip")
    : t("container.midCycleFluxChip");
  const shortTitle = isConditions
    ? t("container.midCycleFluxConditionsBadgeShort")
    : t("container.midCycleFluxBadgeShort");
  const changeSummary = isConditions
    ? `${t("Flux")} ${newLabel} · ${modifiedAtLabel || "—"}`
    : `${previousLabel} → ${newLabel} · ${modifiedAtLabel || "—"}`;
  const warningText = isConditions
    ? t("container.midCycleFluxConditionsWarning")
    : t("container.midCycleFluxHeterogeneousWarning");
  const fullBadgeText = isConditions
    ? t("container.midCycleFluxConditionsBadge", { flux: newLabel })
    : t("container.midCycleFluxBadge", {
        previous: previousLabel,
        next: newLabel,
      });
  const tooltipTitle = isConditions
    ? t("container.midCycleFluxConditionsTooltip", {
        flux: newLabel,
        date: modifiedAtLabel || "—",
      })
    : t("container.midCycleFluxTooltip", {
        previous: previousLabel,
        next: newLabel,
        date: modifiedAtLabel || "—",
      });

  const closeModal = (event) => {
    event?.stopPropagation?.();
    if (isValidating) return;
    setIsModalOpen(false);
  };

  const openModal = (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setIsModalOpen(true);
  };

  const handleValidate = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isValidating || !container?._id) return;

    setIsValidating(true);
    try {
      const response = await validateMidCycleFluxChange(container._id);
      // eslint-disable-next-line
      console.log("[validateMidCycleFluxChange] update stats", {
        containerId: container._id,
        metrics: response?.data?.metrics,
        rewrite: response?.data?.rewrite,
      });
      setClearedLocally(true);
      setIsModalOpen(false);
      toast.success(t("container.midCycleFluxValidated"), {
        className: "custom-toast",
      });
      if (typeof onValidated === "function") {
        onValidated(response?.data?.data || response?.data);
      }
    } catch (error) {
      console.error("validateMidCycleFluxChange failed:", error);
      toast.error(t("container.midCycleFluxValidateError"), {
        className: "custom-toast",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const triggerButton =
    variant === "table" ? (
      <button
        type="button"
        className="inline-flex max-w-full items-center gap-1.5 rounded-full py-1 px-3 text-sm font-semibold bg-[#FFF4E6] text-[#B8860B] border border-[#F0D9A8] hover:bg-[#FFECD2] transition-colors"
        onClick={openModal}
        aria-haspopup="dialog"
        aria-expanded={isModalOpen}
        title={tooltipTitle}
      >
        <i className="fas fa-exclamation-circle shrink-0" aria-hidden />
        <span className="truncate">{chipLabel}</span>
        <i
          className="fas fa-chevron-down shrink-0 text-xs opacity-70"
          aria-hidden
        />
      </button>
    ) : (
      <button
        type="button"
        className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-[#FFF4E6] text-[#B8860B] border border-[#F0D9A8] hover:bg-[#FFECD2] transition-colors"
        onClick={openModal}
        aria-haspopup="dialog"
        aria-expanded={isModalOpen}
        title={tooltipTitle}
      >
        <i className="fas fa-exclamation-circle shrink-0" aria-hidden />
        <span className="truncate">{fullBadgeText}</span>
      </button>
    );

  return (
    <div
      className="flex min-w-0 max-w-full flex-col items-start"
      onClick={(e) => e.stopPropagation()}
    >
      {triggerButton}

      <Modal
        isOpen={isModalOpen}
        toggle={closeModal}
        centered
        className="mid-cycle-flux-modal"
        contentClassName="rounded-lg border-0 shadow-lg overflow-hidden relative"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeModal}
          className="absolute top-3 right-3 z-10 h-8 w-8 inline-flex items-center justify-center rounded-md text-[#8A8A8A] hover:bg-[#F5F5F5] hover:text-[#222222] disabled:opacity-50"
          aria-label={t("Close")}
          disabled={isValidating}
        >
          <i className="fas fa-times text-sm" />
        </button>

        <div className="flex items-center gap-2.5 min-w-0 px-5 pt-4 pb-2 pr-12">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FFF4E6] text-[#B8860B]">
            <i className="fas fa-exclamation-circle text-sm" aria-hidden />
          </span>
          <h3 className="m-0 text-base font-semibold text-[#222222] leading-none">
            {shortTitle}
          </h3>
        </div>

        <div className="px-5 pt-2 pb-4 flex flex-col gap-2">
          <p className="m-0 text-sm font-medium text-[#222222] leading-snug">
            {changeSummary}
          </p>
          <p className="m-0 text-sm text-[#686868] leading-snug">
            {warningText}
          </p>
        </div>

        {showValidateButton ? (
          <div className="flex items-center justify-center px-5 pb-5">
            <button
              type="button"
              onClick={handleValidate}
              disabled={isValidating}
              className="create-button text-sm px-4 disabled:opacity-60"
            >
              {isValidating
                ? t("container.midCycleFluxValidating")
                : t("container.midCycleFluxValidate")}
            </button>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default MidCycleFluxChangeBadge;
